import { Linkedin } from 'lucide-react';
import React from 'react';

import type { PolicySection, PolicySubsection } from '../../content/legal/types';
import { resolveSectionId, resolveSubsectionId } from '../../content/legal/slug';

function renderParagraphs(paragraphs?: string[]) {
  if (!paragraphs?.length) return null;
  return paragraphs.map((p) => <p key={p.slice(0, 48)}>{p}</p>);
}

function renderList(list?: string[]) {
  if (!list?.length) return null;
  return (
    <ul className="policy-doc-list">
      {list.map((item) => (
        <li key={item.slice(0, 48)}>{item}</li>
      ))}
    </ul>
  );
}

function renderSubsection(
  sub: PolicySubsection,
  section: PolicySection,
  sectionIndex: number,
  subIndex: number,
) {
  const subId = resolveSubsectionId(section, sectionIndex, sub, subIndex);
  return (
    <div key={subId} id={subId} className="policy-doc-subsection scroll-mt-28">
      <h3 className="policy-doc-subsection__title">{sub.title}</h3>
      <div className="policy-doc-body">{renderParagraphs(sub.paragraphs)}</div>
      {renderList(sub.list)}
    </div>
  );
}

function renderSection(section: PolicySection, sectionIndex: number) {
  const sectionId = resolveSectionId(section, sectionIndex);
  return (
    <section key={sectionId} id={sectionId} className="policy-doc-section scroll-mt-28">
      <h2 className="policy-doc-section__title">{section.title}</h2>
      <div className="policy-doc-body">{renderParagraphs(section.paragraphs)}</div>
      {renderList(section.list)}
      {section.subsections?.map((sub, subIndex) =>
        renderSubsection(sub, section, sectionIndex, subIndex),
      )}
      {section.callout ? <div className="policy-doc-callout">{section.callout}</div> : null}
      {section.table ? (
        <div className="policy-doc-table-wrap">
          <table className="policy-doc-table">
            <thead>
              <tr>
                {section.table.headers.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.table.rows.map((row) => (
                <tr key={row.join('-')}>
                  {row.map((cell) => (
                    <td key={cell}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {section.cards?.length ? (
        <div className="policy-doc-cards">
          {section.cards.map((card) => (
            <div key={card.title} className="policy-doc-card-item">
              <h4 className="policy-doc-card-item__title">{card.title}</h4>
              <p className="policy-doc-card-item__body">{card.body}</p>
            </div>
          ))}
        </div>
      ) : null}
      {section.stats?.length ? (
        <div className="policy-doc-stats">
          {section.stats.map((stat) => (
            <div key={stat.label} className="policy-doc-stat">
              <span className="policy-doc-stat__value">{stat.value}</span>
              <span className="policy-doc-stat__label">{stat.label}</span>
            </div>
          ))}
        </div>
      ) : null}
      {section.team?.length ? (
        <div className="policy-doc-team">
          {section.team.map((member) => (
            <div key={member.name} className="policy-doc-team-card">
              <div className="policy-doc-team-card__photo">
                <img
                  src={member.image}
                  alt={member.name}
                  width={128}
                  height={128}
                  loading="lazy"
                  decoding="async"
                  style={member.imageObjectPosition ? { objectPosition: member.imageObjectPosition } : undefined}
                />
              </div>
              <div className="policy-doc-team-card__body">
                <h4 className="policy-doc-team-card__name">{member.name}</h4>
                <p className="policy-doc-team-card__role">{member.role}</p>
                <p className="policy-doc-team-card__bio">{member.bio}</p>
              </div>
              <a
                href={member.linkedIn}
                target="_blank"
                rel="noopener noreferrer"
                className="policy-doc-team-card__linkedin"
              >
                <Linkedin className="w-4 h-4 shrink-0" aria-hidden />
                LinkedIn
              </a>
            </div>
          ))}
        </div>
      ) : null}
      {section.cta ? (
        <div className="policy-doc-cta">
          <h4 className="policy-doc-cta__title">{section.cta.title}</h4>
          <p className="policy-doc-cta__body">{section.cta.body}</p>
          <div className="policy-doc-cta__actions">
            <a href={section.cta.primaryHref} className="policy-doc-cta__btn policy-doc-cta__btn--primary">
              {section.cta.primaryLabel}
            </a>
            {section.cta.secondaryLabel && section.cta.secondaryHref ? (
              <a href={section.cta.secondaryHref} className="policy-doc-cta__btn policy-doc-cta__btn--secondary">
                {section.cta.secondaryLabel}
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

interface PolicyDocumentRendererProps {
  eyebrow: string;
  title: string;
  effectiveDate: string;
  lastUpdated: string;
  summary?: string;
  sections: PolicySection[];
  metaPrimaryLabel?: string;
  metaSecondaryLabel?: string;
}

const PolicyDocumentRenderer: React.FC<PolicyDocumentRendererProps> = ({
  eyebrow,
  title,
  effectiveDate,
  lastUpdated,
  summary,
  sections,
  metaPrimaryLabel = 'Effective date',
  metaSecondaryLabel = 'Last updated',
}) => (
  <article className="policy-doc-card">
    <p className="policy-doc-card__eyebrow">{eyebrow}</p>
    <h1 className="policy-doc-card__title">{title}</h1>
    <p className="policy-doc-card__meta">
      <strong>{metaPrimaryLabel}:</strong> {effectiveDate}
      <br />
      <strong>{metaSecondaryLabel}:</strong> {lastUpdated}
    </p>
    {summary ? <div className="policy-doc-card__summary">{summary}</div> : null}
    {sections.map(renderSection)}
  </article>
);

export default PolicyDocumentRenderer;
